import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Archive, Check, Download, FileDown, Maximize2, Repeat } from 'lucide-react';
import {
  aggregate,
  formatAggregate,
  byPosition,
  dueState,
  formatDue,
  formatFieldValue,
  formatMinutes,
  PRIORITIES,
  type BoardSnapshot,
  type CardSummary,
  calendarDate,
  moveToDate,
  shiftStartWithDue,
  dueDeadline,
  plausibleDate,
} from '@kaba/shared';
import { api } from '../../lib/api';
import { neighborsAt, useBoardAction, useMoveCard, useUpdateCard } from '../../lib/board';
import { toast } from '../../lib/toast';
import { Avatar, Button, cx, LABEL_BG } from '../ui/ui';

type SortKey = 'position' | 'title' | 'column' | 'due' | 'priority' | 'updated' | `field:${string}`;

const cellInput =
  'min-h-9 w-full rounded-md border border-transparent bg-transparent px-1.5 text-sm outline-none hover:border-[var(--line)] focus:border-accent focus:bg-[var(--panel)]';


/** Tabellenansicht: viele Felder auf einen Blick, direkt bearbeitbar, Sammelaktionen */
export function TableView({
  snapshot,
  visible,
  canEdit,
  onOpenCard,
  onExport,
}: {
  snapshot: BoardSnapshot;
  visible: (c: CardSummary) => boolean;
  canEdit: boolean;
  onOpenCard: (id: string) => void;
  /** Export-Dialog öffnen (mit Auswahl oder ohne) */
  onExport?: (ids: string[] | null) => void;
}) {
  const boardId = snapshot.board.id;
  const update = useUpdateCard(boardId);
  const move = useMoveCard(boardId);
  const bulk = useBoardAction(boardId);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'position', dir: 1 });
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const columns = useMemo(() => [...snapshot.columns].sort(byPosition), [snapshot.columns]);
  const colIndex = useMemo(() => new Map(columns.map((c, i) => [c.id, i])), [columns]);
  const members = useMemo(() => new Map(snapshot.members.map((m) => [m.id, m])), [snapshot.members]);

  const rows = useMemo(() => {
    const list = snapshot.cards.filter(visible);
    const val = (c: CardSummary): string | number => {
      switch (sort.key) {
        case 'position':
          return `${String(colIndex.get(c.columnId) ?? 0).padStart(3, '0')}${c.position}`;
        case 'title':
          return c.title.toLowerCase();
        case 'column':
          return colIndex.get(c.columnId) ?? 0;
        case 'due':
          return c.dueAt ? String(dueDeadline(c.dueAt, c.dueHasTime).getTime()).padStart(16, "0") : "9999999999999999";
        case 'priority':
          return -c.priority;
        case 'updated':
          return c.updatedAt;
        default: {
          const f = snapshot.fields.find((x) => `field:${x.id}` === sort.key);
          const v = f ? c.custom[f.id] : null;
          if (v === null || v === undefined) return '￿';
          return typeof v === 'number' ? v : String(formatFieldValue(f!, v, members) ?? '').toLowerCase();
        }
      }
    };
    return [...list].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      return (x < y ? -1 : x > y ? 1 : 0) * sort.dir;
    });
  }, [snapshot.cards, snapshot.fields, visible, sort, colIndex, members]);

  const toggleSort = (key: SortKey) => setSort((s) => (s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: 1 }));
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  const head = (key: SortKey, label: string, className = '') => (
    <th scope="col" className={cx('px-2 py-2 text-left font-semibold whitespace-nowrap', className)} aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button className="inline-flex items-center gap-1 hover:text-[var(--ink)]" onClick={() => toggleSort(key)}>
        {label}
        {sort.key === key && (sort.dir === 1 ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
      </button>
    </th>
  );

  const runBulk = (label: string, fn: (c: CardSummary) => Promise<unknown>) => {
    const targets = snapshot.cards.filter((c) => selected.has(c.id));
    bulk.mutate(async () => {
      for (const c of targets) await fn(c);
      toast.info(`${label}: ${targets.length} ${targets.length === 1 ? 'Karte' : 'Karten'}`);
    });
    setSelected(new Set());
  };

  return (
    <div className="flex h-full flex-col">
      {selected.size > 0 && canEdit && (
        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--line)] bg-accent-soft px-3 py-2 text-sm sm:px-4" role="toolbar" aria-label="Sammelaktionen">
          <span className="font-medium">{selected.size} ausgewählt</span>
          <select
            aria-label="In Spalte verschieben"
            className="min-h-9 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2"
            value=""
            onChange={(e) => {
              const colId = e.target.value;
              if (!colId) return;
              runBulk('Verschoben', async (c) => {
                if (c.columnId === colId) return;
                const n = neighborsAt(snapshot.cards, colId, c.id, Number.MAX_SAFE_INTEGER);
                const last = snapshot.cards.filter((x) => x.columnId === colId && x.id !== c.id).sort(byPosition).at(-1);
                await api.post(`/api/cards/${c.id}/move`, { columnId: colId, beforeId: n.beforeId ?? last?.id ?? null, afterId: null });
              });
            }}
          >
            <option value="">Spalte …</option>
            {columns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
          <select
            aria-label="Priorität setzen"
            className="min-h-9 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2"
            value=""
            onChange={(e) => {
              if (e.target.value === '') return;
              const p = Number(e.target.value);
              runBulk('Priorität gesetzt', (c) => api.patch(`/api/cards/${c.id}`, { priority: p }));
            }}
          >
            <option value="">Priorität …</option>
            {PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <Button size="sm" onClick={() => runBulk('Erledigt', (c) => api.patch(`/api/cards/${c.id}`, { completed: true, expectedVersion: c.version }))}>
            <Check size={15} /> Erledigt
          </Button>
          <Button
            size="sm"
            onClick={() => {
              if (window.confirm(`${selected.size} Karten archivieren?`)) runBulk('Archiviert', (c) => api.del(`/api/cards/${c.id}`));
            }}
          >
            <Archive size={15} /> Archivieren
          </Button>
          {onExport && (
            <Button size="sm" onClick={() => onExport([...selected])}>
              <FileDown size={15} /> Exportieren
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Auswahl aufheben
          </Button>
        </div>
      )}
      {selected.size === 0 && (
        <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-[var(--ink-soft)] sm:px-4">
          <span>
            {rows.length} {rows.length === 1 ? 'Karte' : 'Karten'}
          </span>
          {onExport && (
            <button type="button" onClick={() => onExport(null)} className="ml-auto inline-flex min-h-9 items-center gap-1 rounded-lg px-2 hover:bg-black/5 hover:text-[var(--ink)] dark:hover:bg-white/10">
              <FileDown size={14} /> Karten exportieren
            </button>
          )}
          <a href={`/api/boards/${boardId}/time.csv`} className={cx(!onExport && 'ml-auto', 'inline-flex min-h-9 items-center gap-1 rounded-lg px-2 hover:bg-black/5 hover:text-[var(--ink)] dark:hover:bg-white/10')} download>
            <Download size={14} /> Zeiten als CSV
          </a>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto px-0 pb-24 sm:px-4 sm:pb-4">
        <table className="min-w-full border-separate border-spacing-0 text-sm">
          <thead className="sticky top-0 z-10 bg-[var(--surface)] text-xs text-[var(--ink-soft)]">
            <tr>
              {canEdit && (
                <th scope="col" className="sticky left-0 z-20 w-10 bg-[var(--surface)] px-2">
                  <input
                    type="checkbox"
                    aria-label="Alle auswählen"
                    checked={allSelected}
                    onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())}
                    className="size-4 accent-[var(--color-accent)]"
                  />
                </th>
              )}
              {head('title', 'Titel', cx('sticky z-20 min-w-40 bg-[var(--surface)] sm:min-w-56', canEdit ? 'left-10' : 'left-0'))}
              {head('column', 'Spalte')}
              <th scope="col" className="px-2 py-2 text-left font-semibold">
                Zuständig
              </th>
              {head('due', 'Fällig')}
              {head('priority', 'Priorität')}
              <th scope="col" className="px-2 py-2 text-left font-semibold">
                Labels
              </th>
              <th scope="col" className="px-2 py-2 text-left font-semibold whitespace-nowrap">
                Checkliste
              </th>
              <th scope="col" className="px-2 py-2 text-left font-semibold">
                Zeit
              </th>
              {snapshot.fields.map((f) => head(`field:${f.id}`, f.name))}
              {head('updated', 'Geändert')}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const state = c.dueAt ? dueState(c.dueAt, c.dueHasTime, c.completedAt) : 'none';
              const isSel = selected.has(c.id);
              const rowBg = isSel ? 'bg-accent-soft' : 'bg-[var(--panel)]';
              return (
                <tr key={c.id} className="group">
                  {canEdit && (
                    <td className={cx('sticky left-0 z-[5] border-b border-[var(--line)] px-2', rowBg)}>
                      <input
                        type="checkbox"
                        aria-label={`„${c.title}“ auswählen`}
                        checked={isSel}
                        onChange={(e) =>
                          setSelected((s) => {
                            const n = new Set(s);
                            if (e.target.checked) n.add(c.id);
                            else n.delete(c.id);
                            return n;
                          })
                        }
                        className="size-4 accent-[var(--color-accent)]"
                      />
                    </td>
                  )}
                  <td className={cx('sticky z-[5] border-b border-[var(--line)] px-1 py-1', canEdit ? 'left-10' : 'left-0', rowBg)}>
                    <div className="flex items-center gap-1">
                      {c.completedAt && <Check size={14} className="shrink-0 text-emerald-600" aria-label="Erledigt" />}
                      {canEdit ? (
                        <input
                          className={cx(cellInput, c.completedAt && 'line-through opacity-70')}
                          defaultValue={c.title}
                          key={c.title}
                          aria-label="Titel"
                          onBlur={(e) => {
                            const t = e.target.value.trim();
                            if (t && t !== c.title) update.mutate({ id: c.id, patch: { title: t, expectedVersion: c.version } });
                            else e.target.value = c.title;
                          }}
                          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                        />
                      ) : (
                        <span className="px-1.5">{c.title}</span>
                      )}
                      {c.recurrence && <Repeat size={13} className="shrink-0 text-[var(--ink-soft)]" aria-label="Wiederkehrend" />}
                      <button
                        aria-label={`„${c.title}“ öffnen`}
                        onClick={() => onOpenCard(c.id)}
                        data-card-id={c.id}
                        className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-[var(--ink-soft)] hover:bg-black/5 dark:hover:bg-white/10"
                      >
                        <Maximize2 size={14} />
                      </button>
                    </div>
                  </td>
                  <td className="border-b border-[var(--line)] px-1 py-1">
                    <select
                      className={cx(cellInput, 'min-w-28')}
                      value={c.columnId}
                      disabled={!canEdit}
                      aria-label="Spalte"
                      onChange={(e) => {
                        const colId = e.target.value;
                        const last = snapshot.cards.filter((x) => x.columnId === colId && x.id !== c.id).sort(byPosition).at(-1);
                        move.mutate({ cardId: c.id, columnId: colId, beforeId: last?.id ?? null, afterId: null });
                      }}
                    >
                      {columns.map((col) => (
                        <option key={col.id} value={col.id}>
                          {col.title}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="border-b border-[var(--line)] px-2 py-1">
                    <span className="flex -space-x-1.5">
                      {c.assigneeIds.map((id) => members.get(id)).filter(Boolean).slice(0, 4).map((m) => (
                        <Avatar key={m!.id} user={m!} size={24} />
                      ))}
                    </span>
                  </td>
                  <td className="border-b border-[var(--line)] px-1 py-1">
                    <input
                      type="date"
                      aria-label="Fällig"
                      disabled={!canEdit}
                      value={c.dueAt ? calendarDate(c.dueAt, c.dueHasTime) : ''}
                      onChange={(e) => {
                        // Zwischenwerte beim Tippen des Jahres („0002-…“) nicht speichern
                        if (e.target.value && !plausibleDate(e.target.value)) return;
                        const iso = e.target.value ? moveToDate(c.dueAt, c.dueHasTime, e.target.value) : null;
                        const startAt = shiftStartWithDue(c, iso, !!iso && c.dueHasTime);
                        update.mutate({ id: c.id, patch: { dueAt: iso, dueHasTime: !!iso && c.dueHasTime, ...startAt, expectedVersion: c.version } });
                      }}
                      className={cx(cellInput, 'w-36', state === 'overdue' && 'font-semibold text-rose-600', state === 'soon' && 'text-amber-600')}
                      title={c.dueAt ? formatDue(c.dueAt, c.dueHasTime) : undefined}
                    />
                  </td>
                  <td className="border-b border-[var(--line)] px-1 py-1">
                    <select
                      className={cx(cellInput, 'min-w-24')}
                      value={c.priority}
                      disabled={!canEdit}
                      aria-label="Priorität"
                      onChange={(e) => update.mutate({ id: c.id, patch: { priority: Number(e.target.value) } })}
                    >
                      {PRIORITIES.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="border-b border-[var(--line)] px-2 py-1">
                    <span className="flex flex-wrap gap-1">
                      {c.labelIds.map((id) => snapshot.labels.find((l) => l.id === id)).filter(Boolean).map((l) => (
                        <span key={l!.id} className={cx('rounded px-1.5 text-[11px] text-white', LABEL_BG[l!.color], !l!.name && 'h-2 w-6')}>
                          {l!.name}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td className="border-b border-[var(--line)] px-2 py-1 text-xs whitespace-nowrap text-[var(--ink-soft)]">
                    {c.checklistTotal ? `${c.checklistDone}/${c.checklistTotal}` : ''}
                  </td>
                  <td className="border-b border-[var(--line)] px-2 py-1 text-xs whitespace-nowrap text-[var(--ink-soft)]">
                    {c.spentMinutes || c.estimateMinutes
                      ? `${formatMinutes(c.spentMinutes)}${c.estimateMinutes ? ` / ${formatMinutes(c.estimateMinutes)}` : ''}`
                      : ''}
                  </td>
                  {snapshot.fields.map((f) => (
                    <td key={f.id} className="max-w-48 truncate border-b border-[var(--line)] px-2 py-1 text-sm">
                      {formatFieldValue(f, c.custom[f.id], members) ?? ''}
                    </td>
                  ))}
                  <td className="border-b border-[var(--line)] px-2 py-1 text-xs whitespace-nowrap text-[var(--ink-soft)]">
                    {new Date(c.updatedAt).toLocaleDateString('de-DE')}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {/* 0.82: Auswertung ausgewerteter Felder (nur, wenn es etwas zu zeigen gibt) */}
          {(() => {
            const foot = snapshot.fields.map((f) => (f.config?.aggregate ? formatAggregate(f, aggregate(f, rows.map((c) => c.custom[f.id]))) : null));
            if (!foot.some(Boolean)) return null;
            return (
              <tfoot className="sticky bottom-0 bg-[var(--surface)] text-xs font-semibold">
                <tr>
                  {canEdit && <td className="sticky left-0 bg-[var(--surface)]" />}
                  <td className={cx('sticky bg-[var(--surface)] px-2 py-2', canEdit ? 'left-10' : 'left-0')}>Auswertung</td>
                  <td colSpan={7} />
                  {foot.map((t, i) => (
                    <td key={snapshot.fields[i]!.id} className="px-2 py-2 whitespace-nowrap tabular-nums">
                      {t ?? ''}
                    </td>
                  ))}
                  <td />
                </tr>
              </tfoot>
            );
          })()}
        </table>
        {rows.length === 0 && <p className="p-6 text-sm text-[var(--ink-soft)]">Keine Karten.</p>}
      </div>
    </div>
  );
}
